/**
 * Acciones de una fila de listado: editar y eliminar.
 *
 * Se repetía en cada tabla con pequeñas variaciones —a veces sin confirmación,
 * a veces con un `confirm()` del navegador— y esa inconsistencia es la que hace
 * que alguien borre sin querer. Aquí el borrado SIEMPRE pide confirmación en el
 * sitio, sin diálogo del sistema: en móvil un `confirm()` se descarta con un
 * toque accidental igual de fácil que se acepta.
 *
 * Los objetivos táctiles son de 44 px (el mínimo accesible), como el resto de
 * la aplicación.
 */
import { Check, Pencil, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface RowActionsProps {
  onEdit: () => void
  onDelete: () => void
  deleting?: boolean
  /** Texto para lectores de pantalla: "la lectura del 3 de marzo". */
  label: string
  className?: string
}

export function RowActions({ onEdit, onDelete, deleting, label, className }: RowActionsProps) {
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    return (
      <span className={cn('flex items-center gap-1', className)}>
        <span className="mr-1 text-xs text-muted-foreground">¿Eliminar?</span>
        <button
          type="button"
          onClick={() => {
            setConfirming(false)
            onDelete()
          }}
          disabled={deleting}
          className="inline-flex size-11 cursor-pointer items-center justify-center rounded-lg text-danger hover:bg-danger-muted disabled:opacity-50"
          aria-label={`Confirmar eliminación de ${label}`}
        >
          <Check className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="inline-flex size-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
          aria-label="Cancelar"
        >
          <X className="size-4" aria-hidden />
        </button>
      </span>
    )
  }

  return (
    <span className={cn('flex items-center gap-1', className)}>
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex size-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={`Editar ${label}`}
      >
        <Pencil className="size-4" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex size-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-danger-muted hover:text-danger"
        aria-label={`Eliminar ${label}`}
      >
        <Trash2 className="size-4" aria-hidden />
      </button>
    </span>
  )
}
